import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import { Tabs } from "@magnetic/tabs";
import { Tooltip } from "@magnetic/tooltip";

import "./pageTabs.scss";

export interface TabRoute {
  header: React.ReactNode;
  url: string;
  name: string;
  onNavigate?: (name: string) => void;
  disabled?: boolean;
  hide?: boolean;
  qaData?: string;
  tooltip?: string;
  isSeparator?: boolean;
}

const TabSeparator = () => <div className="cnc-page-tabs-separator" />;

interface PageTabProps {
  readonly tabs: TabRoute[];
  readonly kind?: "primary" | "secondary";
}

export const PageTab = (props: PageTabProps) => {
  const { tabs, kind = "primary" } = props;
  const location = useLocation();

  /**
   * It seems like we want the component
   * to update the selected tab styling if
   * the location changes outside the component.
   * This might happen if someone manually changes
   * the url. I had to defererence the pathname from
   * the location object or react-hooks/exhaustive-deps
   * rule complained.
   */
  const { pathname } = location;
  useEffect(() => {
    tabs.some(({ url, onNavigate, name }) => {
      if (pathname === url) {
        /**
         * Moved into useEffect because calling this
         * while rendering calls a setState on the
         * parent.
         */
        if (onNavigate) {
          onNavigate(name);
          return true;
        }
      }
      return false;
    });
  }, [tabs, pathname]);

  const tabLinks = useMemo((): React.ReactElement[] => {
    return tabs
      .filter((tab) => !tab.hide)
      .map((tab): React.ReactElement => {
        const {
          name,
          url,
          header,
          disabled,
          onNavigate,
          tooltip,
          isSeparator
        } = tab;

        if (isSeparator) {
          return <TabSeparator key={url} />;
        }

        const handleNavigation = (): void => {
          if (onNavigate) {
            onNavigate(name);
          }
        };

        const tabLink = (
          <Tabs.Link
            key={url}
            as={Link}
            to={url}
            className="mds-rebuild-tabs-link"
            selected={pathname.includes(url)}
            disabled={disabled}
            onClick={handleNavigation}
            data-qa={tab.qaData}
          >
            {header as string}
          </Tabs.Link>
        );

        if (tooltip) {
          return (
            <Tooltip key={url} title={tooltip}>
              {tabLink}
            </Tooltip>
          );
        }

        return tabLink;
      });
  }, [tabs, pathname]);

  return <Tabs kind={kind}>{tabLinks}</Tabs>;
};
